export const disasterSteps = [
  "ENTRY",
  "EVENT",
  "CONTEXT",
  "IMMEDIATE_DANGER",
  "DECISION",
  "NEXT_ACTION",
  "CONTINUATION",
  "COMPLETE",
] as const;

export type DisasterStep = (typeof disasterSteps)[number];
export type DisasterAnswer =
  | "FICTIONAL_EVENT_A"
  | "FICTIONAL_EVENT_B"
  | "CONTEXT_INDOOR"
  | "CONTEXT_OUTDOOR"
  | "DANGER_YES"
  | "DANGER_NO"
  | "BRANCH_ALPHA"
  | "BRANCH_BETA"
  | "ACKNOWLEDGE"
  | "CONTINUE"
  | "FINISH";

export interface DisasterSession {
  readonly schemaVersion: 1;
  readonly current: DisasterStep;
  readonly history: readonly DisasterStep[];
  readonly answers: Readonly<Partial<Record<DisasterStep, DisasterAnswer>>>;
}

export type DisasterEvent =
  | { readonly type: "START" }
  | { readonly type: "ANSWER"; readonly answer: DisasterAnswer }
  | { readonly type: "BACK" }
  | { readonly type: "RESTART" };

const nextStep: Readonly<Partial<Record<DisasterStep, DisasterStep>>> = {
  ENTRY: "EVENT",
  EVENT: "CONTEXT",
  CONTEXT: "IMMEDIATE_DANGER",
  IMMEDIATE_DANGER: "DECISION",
  DECISION: "NEXT_ACTION",
  NEXT_ACTION: "CONTINUATION",
  CONTINUATION: "COMPLETE",
};

const permittedAnswers: Readonly<Partial<Record<DisasterStep, readonly DisasterAnswer[]>>> = {
  EVENT: disasterModeAnswerCatalog.EVENT,
  CONTEXT: disasterModeAnswerCatalog.CONTEXT,
  IMMEDIATE_DANGER: disasterModeAnswerCatalog.IMMEDIATE_DANGER,
  DECISION: disasterModeAnswerCatalog.DECISION,
  NEXT_ACTION: disasterModeAnswerCatalog.NEXT_ACTION,
  CONTINUATION: disasterModeAnswerCatalog.CONTINUATION,
};

export function isAnswerPermitted(state: DisasterStep, answer: string): answer is DisasterAnswer {
  return permittedAnswers[state]?.some((candidate) => candidate === answer) ?? false;
}

export const initialDisasterSession: DisasterSession = {
  schemaVersion: 1,
  current: "ENTRY",
  history: [],
  answers: {},
};

export class InvalidDisasterTransitionError extends Error {
  constructor(step: DisasterStep, event: DisasterEvent["type"]) {
    super(`Event ${event} is not valid from disaster step ${step}.`);
    this.name = "InvalidDisasterTransitionError";
  }
}

export function transitionDisasterSession(
  session: DisasterSession,
  event: DisasterEvent,
): DisasterSession {
  if (event.type === "RESTART") return initialDisasterSession;

  if (event.type === "BACK") {
    const previous = session.history.at(-1);
    if (previous === undefined)
      throw new InvalidDisasterTransitionError(session.current, event.type);
    const answers = Object.fromEntries(
      Object.entries(session.answers).filter(([step]) => step !== previous),
    ) as DisasterSession["answers"];
    return {
      ...session,
      current: previous,
      history: session.history.slice(0, -1),
      answers,
    };
  }

  if (event.type === "START") {
    if (session.current !== "ENTRY") {
      throw new InvalidDisasterTransitionError(session.current, event.type);
    }
    return { ...session, current: "EVENT", history: ["ENTRY"] };
  }

  const allowed = permittedAnswers[session.current];
  const next = nextStep[session.current];
  if (allowed === undefined || next === undefined || !allowed.includes(event.answer)) {
    throw new InvalidDisasterTransitionError(session.current, event.type);
  }

  return {
    ...session,
    current: next,
    history: [...session.history, session.current],
    answers: { ...session.answers, [session.current]: event.answer },
  };
}

export interface DisasterMachineDiagnostics {
  readonly state: DisasterStep;
  readonly progressIndex: number;
  readonly totalInteractiveSteps: number;
  readonly canGoBack: boolean;
  readonly terminal: boolean;
  readonly permittedEvents: readonly DisasterEvent["type"][];
}

export function inspectDisasterSession(session: DisasterSession): DisasterMachineDiagnostics {
  return {
    state: session.current,
    progressIndex: Math.max(0, disasterSteps.indexOf(session.current)),
    totalInteractiveSteps: disasterSteps.length - 1,
    canGoBack: session.history.length > 0,
    terminal: session.current === "COMPLETE",
    permittedEvents: [
      ...(session.current === "ENTRY" ? (["START"] as const) : []),
      ...(permittedAnswers[session.current] === undefined ? [] : (["ANSWER"] as const)),
      ...(session.history.length === 0 ? [] : (["BACK"] as const)),
      "RESTART",
    ],
  };
}

export function isDisasterSession(value: unknown): value is DisasterSession {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<DisasterSession>;
  return (
    candidate.schemaVersion === 1 &&
    disasterSteps.some((step) => step === candidate.current) &&
    Array.isArray(candidate.history) &&
    candidate.history.every((step) => disasterSteps.some((valid) => valid === step)) &&
    Object.prototype.toString.call(candidate.answers) === "[object Object]"
  );
}
import { disasterModeAnswerCatalog } from "@safesphere/contracts";
